import type {
  CalcType,
  CalcValue,
  FormulaEvaluationContext,
  FormulaFunctionDoc,
} from './types'
import {
  formulaDateAdd,
  formulaDateDifference,
  formulaDateInstant,
  formulaDatePart,
  formulaToday,
} from './dates'

/**
 * Formula function registry: type signatures for analysis + implementations
 * for evaluation. Function names are matched case-insensitively.
 */

export class FormulaRuntimeError extends Error {}

/** Coerce to number; null propagates, non-numeric throws */
export function toNumber(value: CalcValue): number | null {
  if (value === null || value === '') return null
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    return value
  }
  if (typeof value === 'string') {
    const num = Number(value)
    if (Number.isNaN(num)) {
      throw new FormulaRuntimeError(`"${value}" is not a number`)
    }
    return num
  }
  if (typeof value === 'boolean') return value ? 1 : 0
  throw new FormulaRuntimeError('Expected a number')
}

/** Coerce to string; null becomes empty string */
export function toText(value: CalcValue): string {
  if (value === null) return ''
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

/** Coerce to boolean; null is false */
export function toBool(value: CalcValue): boolean {
  if (value === null || value === '') return false
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  return true
}

/** Interpret calendar dates at the start of their day in the evaluation zone. */
export function toDate(
  value: CalcValue,
  context: FormulaEvaluationContext = { timeZone: 'UTC' },
): Date | null {
  return formulaDateInstant(value, context)
}

type DateUnit = 'days' | 'hours' | 'minutes' | 'months' | 'years' | 'weeks'

function normalizeDateUnit(raw: CalcValue): DateUnit {
  const unit = toText(raw).toLowerCase().replace(/s$/, '')
  switch (unit) {
    case '':
    case 'day':
      return 'days'
    case 'hour':
      return 'hours'
    case 'minute':
      return 'minutes'
    case 'week':
      return 'weeks'
    case 'month':
      return 'months'
    case 'year':
      return 'years'
    default:
      throw new FormulaRuntimeError(
        `Unknown date unit "${toText(raw)}" (use days, weeks, months, years, hours, or minutes)`,
      )
  }
}

export interface FormulaFunctionDef {
  name: string
  minArgs: number
  /** Infinity for variadic */
  maxArgs: number
  /** Parameter types; the last entry repeats for variadic functions */
  params: CalcType[]
  /** Static return type, or derive from argument types (e.g. IF) */
  returns: CalcType | ((argTypes: CalcType[]) => CalcType)
  impl: (args: CalcValue[], context: FormulaEvaluationContext) => CalcValue
  doc: FormulaFunctionDoc
}

/** Unify types for IF/COALESCE style functions */
export function unifyTypes(types: CalcType[]): CalcType {
  const concrete = types.filter(type => type !== 'NULL' && type !== 'ANY')
  if (concrete.length === 0) return 'ANY'
  const first = concrete[0]
  return concrete.every(type => type === first) ? first : 'ANY'
}

function numericVariadic(values: CalcValue[]): number[] {
  const nums: number[] = []
  for (const value of values) {
    const num = toNumber(value)
    if (num !== null) nums.push(num)
  }
  return nums
}

const defs: FormulaFunctionDef[] = [
  // === Logic ===
  {
    name: 'IF',
    minArgs: 2,
    maxArgs: 3,
    params: ['BOOLEAN', 'ANY', 'ANY'],
    returns: argTypes => unifyTypes(argTypes.slice(1)),
    impl: args => (toBool(args[0]) ? args[1] : (args[2] ?? null)),
    doc: {
      name: 'IF',
      signature: 'IF(condition, then, else)',
      description:
        'Returns one value if the condition is true, another if false',
      category: 'logic',
    },
  },
  {
    name: 'AND',
    minArgs: 2,
    maxArgs: Infinity,
    params: ['BOOLEAN'],
    returns: 'BOOLEAN',
    impl: args => args.every(toBool),
    doc: {
      name: 'AND',
      signature: 'AND(a, b, ...)',
      description: 'True when every argument is true',
      category: 'logic',
    },
  },
  {
    name: 'OR',
    minArgs: 2,
    maxArgs: Infinity,
    params: ['BOOLEAN'],
    returns: 'BOOLEAN',
    impl: args => args.some(toBool),
    doc: {
      name: 'OR',
      signature: 'OR(a, b, ...)',
      description: 'True when any argument is true',
      category: 'logic',
    },
  },
  {
    name: 'NOT',
    minArgs: 1,
    maxArgs: 1,
    params: ['BOOLEAN'],
    returns: 'BOOLEAN',
    impl: args => !toBool(args[0]),
    doc: {
      name: 'NOT',
      signature: 'NOT(value)',
      description: 'Inverts a true/false value',
      category: 'logic',
    },
  },
  {
    name: 'ISBLANK',
    minArgs: 1,
    maxArgs: 1,
    params: ['ANY'],
    returns: 'BOOLEAN',
    impl: args => args[0] === null || args[0] === '',
    doc: {
      name: 'ISBLANK',
      signature: 'ISBLANK(value)',
      description: 'True when the value is empty',
      category: 'logic',
    },
  },
  {
    name: 'COALESCE',
    minArgs: 1,
    maxArgs: Infinity,
    params: ['ANY'],
    returns: argTypes => unifyTypes(argTypes),
    impl: args => args.find(arg => arg !== null && arg !== '') ?? null,
    doc: {
      name: 'COALESCE',
      signature: 'COALESCE(a, b, ...)',
      description: 'Returns the first non-empty value',
      category: 'logic',
    },
  },

  // === Math ===
  {
    name: 'ROUND',
    minArgs: 1,
    maxArgs: 2,
    params: ['NUMBER', 'NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const num = toNumber(args[0])
      if (num === null) return null
      const digits = toNumber(args[1] ?? 0) ?? 0
      const factor = 10 ** digits
      return Math.round(num * factor) / factor
    },
    doc: {
      name: 'ROUND',
      signature: 'ROUND(number, digits)',
      description: 'Rounds a number to the given decimal places',
      category: 'math',
    },
  },
  {
    name: 'FLOOR',
    minArgs: 1,
    maxArgs: 1,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const num = toNumber(args[0])
      return num === null ? null : Math.floor(num)
    },
    doc: {
      name: 'FLOOR',
      signature: 'FLOOR(number)',
      description: 'Rounds a number down to the nearest integer',
      category: 'math',
    },
  },
  {
    name: 'CEIL',
    minArgs: 1,
    maxArgs: 1,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const num = toNumber(args[0])
      return num === null ? null : Math.ceil(num)
    },
    doc: {
      name: 'CEIL',
      signature: 'CEIL(number)',
      description: 'Rounds a number up to the nearest integer',
      category: 'math',
    },
  },
  {
    name: 'ABS',
    minArgs: 1,
    maxArgs: 1,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const num = toNumber(args[0])
      return num === null ? null : Math.abs(num)
    },
    doc: {
      name: 'ABS',
      signature: 'ABS(number)',
      description: 'Absolute value of a number',
      category: 'math',
    },
  },
  {
    name: 'SQRT',
    minArgs: 1,
    maxArgs: 1,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const num = toNumber(args[0])
      if (num === null) return null
      if (num < 0) throw new FormulaRuntimeError('SQRT of a negative number')
      return Math.sqrt(num)
    },
    doc: {
      name: 'SQRT',
      signature: 'SQRT(number)',
      description: 'Square root of a number',
      category: 'math',
    },
  },
  {
    name: 'POWER',
    minArgs: 2,
    maxArgs: 2,
    params: ['NUMBER', 'NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const base = toNumber(args[0])
      const exp = toNumber(args[1])
      return base === null || exp === null ? null : base ** exp
    },
    doc: {
      name: 'POWER',
      signature: 'POWER(base, exponent)',
      description: 'Raises a number to a power',
      category: 'math',
    },
  },
  {
    name: 'MOD',
    minArgs: 2,
    maxArgs: 2,
    params: ['NUMBER', 'NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const num = toNumber(args[0])
      const div = toNumber(args[1])
      if (num === null || div === null) return null
      if (div === 0) throw new FormulaRuntimeError('MOD by zero')
      return num % div
    },
    doc: {
      name: 'MOD',
      signature: 'MOD(number, divisor)',
      description: 'Remainder after division',
      category: 'math',
    },
  },
  {
    name: 'MIN',
    minArgs: 1,
    maxArgs: Infinity,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const nums = numericVariadic(args)
      return nums.length ? Math.min(...nums) : null
    },
    doc: {
      name: 'MIN',
      signature: 'MIN(a, b, ...)',
      description: 'Smallest of the given numbers',
      category: 'math',
    },
  },
  {
    name: 'MAX',
    minArgs: 1,
    maxArgs: Infinity,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const nums = numericVariadic(args)
      return nums.length ? Math.max(...nums) : null
    },
    doc: {
      name: 'MAX',
      signature: 'MAX(a, b, ...)',
      description: 'Largest of the given numbers',
      category: 'math',
    },
  },
  {
    name: 'SUM',
    minArgs: 1,
    maxArgs: Infinity,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => numericVariadic(args).reduce((total, num) => total + num, 0),
    doc: {
      name: 'SUM',
      signature: 'SUM(a, b, ...)',
      description: 'Adds the given numbers (empty values count as 0)',
      category: 'math',
    },
  },
  {
    name: 'AVG',
    minArgs: 1,
    maxArgs: Infinity,
    params: ['NUMBER'],
    returns: 'NUMBER',
    impl: args => {
      const nums = numericVariadic(args)
      if (!nums.length) return null
      return nums.reduce((total, num) => total + num, 0) / nums.length
    },
    doc: {
      name: 'AVG',
      signature: 'AVG(a, b, ...)',
      description: 'Average of the given numbers (ignores empty values)',
      category: 'math',
    },
  },

  // === Text ===
  {
    name: 'CONCAT',
    minArgs: 1,
    maxArgs: Infinity,
    params: ['ANY'],
    returns: 'STRING',
    impl: args => args.map(toText).join(''),
    doc: {
      name: 'CONCAT',
      signature: 'CONCAT(a, b, ...)',
      description: 'Joins values into one text string',
      category: 'text',
    },
  },
  {
    name: 'UPPER',
    minArgs: 1,
    maxArgs: 1,
    params: ['STRING'],
    returns: 'STRING',
    impl: args => toText(args[0]).toUpperCase(),
    doc: {
      name: 'UPPER',
      signature: 'UPPER(text)',
      description: 'Converts text to uppercase',
      category: 'text',
    },
  },
  {
    name: 'LOWER',
    minArgs: 1,
    maxArgs: 1,
    params: ['STRING'],
    returns: 'STRING',
    impl: args => toText(args[0]).toLowerCase(),
    doc: {
      name: 'LOWER',
      signature: 'LOWER(text)',
      description: 'Converts text to lowercase',
      category: 'text',
    },
  },
  {
    name: 'TRIM',
    minArgs: 1,
    maxArgs: 1,
    params: ['STRING'],
    returns: 'STRING',
    impl: args => toText(args[0]).trim(),
    doc: {
      name: 'TRIM',
      signature: 'TRIM(text)',
      description: 'Removes leading and trailing spaces',
      category: 'text',
    },
  },
  {
    name: 'LEN',
    minArgs: 1,
    maxArgs: 1,
    params: ['STRING'],
    returns: 'NUMBER',
    impl: args => toText(args[0]).length,
    doc: {
      name: 'LEN',
      signature: 'LEN(text)',
      description: 'Number of characters in the text',
      category: 'text',
    },
  },
  {
    name: 'LEFT',
    minArgs: 2,
    maxArgs: 2,
    params: ['STRING', 'NUMBER'],
    returns: 'STRING',
    impl: args => toText(args[0]).slice(0, Math.max(0, toNumber(args[1]) ?? 0)),
    doc: {
      name: 'LEFT',
      signature: 'LEFT(text, count)',
      description: 'First characters of the text',
      category: 'text',
    },
  },
  {
    name: 'RIGHT',
    minArgs: 2,
    maxArgs: 2,
    params: ['STRING', 'NUMBER'],
    returns: 'STRING',
    impl: args => {
      const text = toText(args[0])
      const count = Math.max(0, toNumber(args[1]) ?? 0)
      return count === 0 ? '' : text.slice(-count)
    },
    doc: {
      name: 'RIGHT',
      signature: 'RIGHT(text, count)',
      description: 'Last characters of the text',
      category: 'text',
    },
  },
  {
    name: 'CONTAINS',
    minArgs: 2,
    maxArgs: 2,
    params: ['STRING', 'STRING'],
    returns: 'BOOLEAN',
    impl: args =>
      toText(args[0]).toLowerCase().includes(toText(args[1]).toLowerCase()),
    doc: {
      name: 'CONTAINS',
      signature: 'CONTAINS(text, search)',
      description: 'True when the text contains the search value',
      category: 'text',
    },
  },
  {
    name: 'TEXT',
    minArgs: 1,
    maxArgs: 1,
    params: ['ANY'],
    returns: 'STRING',
    impl: args => toText(args[0]),
    doc: {
      name: 'TEXT',
      signature: 'TEXT(value)',
      description: 'Converts any value to text',
      category: 'text',
    },
  },
  {
    name: 'VALUE',
    minArgs: 1,
    maxArgs: 1,
    params: ['ANY'],
    returns: 'NUMBER',
    impl: args => toNumber(args[0]),
    doc: {
      name: 'VALUE',
      signature: 'VALUE(text)',
      description: 'Converts text to a number',
      category: 'text',
    },
  },

  // === Date ===
  {
    name: 'TODAY',
    minArgs: 0,
    maxArgs: 0,
    params: [],
    returns: 'DATE',
    impl: (_args, context) => formulaToday(context),
    doc: {
      name: 'TODAY',
      signature: 'TODAY()',
      description: "Today's date (without time)",
      category: 'date',
    },
  },
  {
    name: 'NOW',
    minArgs: 0,
    maxArgs: 0,
    params: [],
    returns: 'DATE',
    impl: (_args, context) => new Date(context.now!.getTime()),
    doc: {
      name: 'NOW',
      signature: 'NOW()',
      description: 'The current date and time',
      category: 'date',
    },
  },
  {
    name: 'DATEDIFF',
    minArgs: 2,
    maxArgs: 3,
    params: ['DATE', 'DATE', 'STRING'],
    returns: 'NUMBER',
    impl: (args, context) =>
      formulaDateDifference(
        args[0],
        args[1],
        normalizeDateUnit(args[2] ?? 'days'),
        context,
      ),
    doc: {
      name: 'DATEDIFF',
      signature: 'DATEDIFF(from, to, unit)',
      description:
        'Calendar boundaries between dates (days by default); hours and minutes measure elapsed time',
      category: 'date',
    },
  },
  {
    name: 'DATEADD',
    minArgs: 2,
    maxArgs: 3,
    params: ['DATE', 'NUMBER', 'STRING'],
    returns: 'DATE',
    impl: (args, context) => {
      const amount = toNumber(args[1])
      return amount === null
        ? null
        : formulaDateAdd(
            args[0],
            amount,
            normalizeDateUnit(args[2] ?? 'days'),
            context,
          )
    },
    doc: {
      name: 'DATEADD',
      signature: 'DATEADD(date, amount, unit)',
      description:
        'Adds calendar units, clamping month ends; hours and minutes add elapsed time',
      category: 'date',
    },
  },
  {
    name: 'YEAR',
    minArgs: 1,
    maxArgs: 1,
    params: ['DATE'],
    returns: 'NUMBER',
    impl: (args, context) => formulaDatePart(args[0], 'year', context),
    doc: {
      name: 'YEAR',
      signature: 'YEAR(date)',
      description: 'The year of a date',
      category: 'date',
    },
  },
  {
    name: 'MONTH',
    minArgs: 1,
    maxArgs: 1,
    params: ['DATE'],
    returns: 'NUMBER',
    impl: (args, context) => formulaDatePart(args[0], 'month', context),
    doc: {
      name: 'MONTH',
      signature: 'MONTH(date)',
      description: 'The month of a date (1-12)',
      category: 'date',
    },
  },
  {
    name: 'DAY',
    minArgs: 1,
    maxArgs: 1,
    params: ['DATE'],
    returns: 'NUMBER',
    impl: (args, context) => formulaDatePart(args[0], 'day', context),
    doc: {
      name: 'DAY',
      signature: 'DAY(date)',
      description: 'The day of the month of a date',
      category: 'date',
    },
  },
]

export const FORMULA_FUNCTION_DEFS: ReadonlyMap<string, FormulaFunctionDef> =
  new Map(defs.map(def => [def.name, def]))

/** Autocomplete documentation for the formula editor */
export const FORMULA_FUNCTIONS: FormulaFunctionDoc[] = defs.map(def => def.doc)
